import React, { useState, useEffect, useRef, useCallback } from "react";
import AdminLayout from "../../components/admin/AdminLayout.jsx";
import { API_BASE, apiFetch } from "../../utils/api";
import "./AdminVideos.css";

/* webpack require.context for thumbnails */
let THUMB_IMAGES = {};
try {
  const ctx = require.context(
    "../../assets/uploads/thumbnails",
    false,
    /\.(png|jpe?g|gif|webp|svg)$/i
  );
  THUMB_IMAGES = ctx.keys().reduce((map, key) => {
    map[key.replace(/^\.\//, "")] = ctx(key);
    return map;
  }, {});
} catch (_) { /* falls back to API URL */ }

function thumbImg(filename) {
  if (!filename) return null;
  return THUMB_IMAGES[filename] ?? `${API_BASE}/uploads/thumbnails/${filename}`;
}

/**
 * Extract YouTube video ID from any common URL format:
 *   https://www.youtube.com/watch?v=5MAiZWfgjsM
 *   https://youtu.be/5MAiZWfgjsM
 *   https://www.youtube.com/embed/5MAiZWfgjsM
 * Returns null if no valid ID found.
 */
function extractYouTubeId(url) {
  if (!url) return null;
  const patterns = [
    /[?&]v=([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const pattern of patterns) {
    const m = url.match(pattern);
    if (m) return m[1];
  }
  return null;
}

function getEmbedUrl(videoUrl) {
  const id = extractYouTubeId(videoUrl);
  return id ? `https://www.youtube.com/embed/${id}` : null;
}

/** Prettify a page slug: "about-us" → "About Us" */
function prettifySlug(slug) {
  if (!slug) return "";
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

const EMPTY_FORM = {
  title:        "",
  description:  "",
  video_url:    "",
  show_on_pages: [], // array of page strings
  order:        "999",
  active:       1,
  thumbnail:    null, // File object for new upload
};

/* ─────────────────────────────────
   YouTube play icon SVG
   ───────────────────────────────── */
function YTPlayIcon() {
  return (
    <svg viewBox="0 0 68 48" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M66.52 7.74A8.55 8.55 0 0 0 60.7 1.9C55.38 0 34 0 34 0S12.62 0 7.3 1.9A8.55 8.55 0 0 0 1.48 7.74C-.44 13.12-.44 24-.44 24s0 10.88 1.92 16.26A8.55 8.55 0 0 0 7.3 46.1C12.62 48 34 48 34 48s21.38 0 26.7-1.9a8.55 8.55 0 0 0 5.82-5.84C68.44 34.88 68.44 24 68.44 24s0-10.88-1.92-16.26z"
        fill="#ff0000"
      />
      <path d="M27.11 34.27L44.89 24 27.11 13.73v20.54z" fill="#fff" />
    </svg>
  );
}

/* ─────────────────────────────────
   Pages tag input
   ───────────────────────────────── */
function PagesInput({ value, onChange }) {
  const [inputVal, setInputVal] = useState("");
  const wrapRef = useRef(null);

  const addPage = (raw) => {
    const trimmed = raw.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
    if (!trimmed || value.includes(trimmed)) { setInputVal(""); return; }
    onChange([...value, trimmed]);
    setInputVal("");
  };

  const removePage = (page) => onChange(value.filter((p) => p !== page));

  const handleKey = (e) => {
    if ((e.key === "Enter" || e.key === ",") && inputVal.trim()) {
      e.preventDefault();
      addPage(inputVal);
    }
    if (e.key === "Backspace" && !inputVal && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className="avd-pages-input-wrap" ref={wrapRef}>
      <div
        className="avd-pages-input-box"
        onClick={() => document.getElementById("avd-pages-text-input")?.focus()}
      >
        {value.map((p) => (
          <span key={p} className="avd-pages-input-tag">
            {prettifySlug(p)}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removePage(p); }}
            >×</button>
          </span>
        ))}
        <input
          id="avd-pages-text-input"
          type="text"
          value={inputVal}
          placeholder={value.length === 0 ? "Type a page slug and press Enter…" : ""}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={handleKey}
          autoComplete="off"
        />
      </div>
    </div>
  );
}

/* ─────────────────────────────────
   Dot-menu
   ───────────────────────────────── */
function DotMenu({ onView, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="avd-dot-menu" ref={ref}>
      <button
        className="avd-dot-menu__trigger"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        title="Actions"
      >
        <span /><span /><span />
      </button>
      {open && (
        <div className="avd-dot-menu__dropdown">
          <button onClick={() => { onView();   setOpen(false); }}>
            <span className="avd-dot-menu__icon">👁</span> View
          </button>
          <button onClick={() => { onEdit();   setOpen(false); }}>
            <span className="avd-dot-menu__icon">✏</span> Edit
          </button>
          <button
            className="avd-dot-menu__delete"
            onClick={() => { onDelete(); setOpen(false); }}
          >
            <span className="avd-dot-menu__icon">🗑</span> Delete
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────
   Status badge
   ───────────────────────────────── */
function StatusBadge({ active }) {
  const isActive = Number(active) === 1;
  return (
    <span className={`avd-badge ${isActive ? "avd-badge--active" : "avd-badge--inactive"}`}>
      {isActive ? "Active" : "Inactive"}
    </span>
  );
}

/* ─────────────────────────────────
   Pages pills (table + view modal)
   ───────────────────────────────── */
function PagesPills({ pages, className = "avd-page-tag" }) {
  if (!pages || pages.length === 0)
    return <span className="avd-no-pages">—</span>;
  return (
    <>
      {pages.map((p) => (
        <span key={p} className={className}>{prettifySlug(p)}</span>
      ))}
    </>
  );
}

/* ═══════════════════════════════════
   VIEW MODAL
   ═══════════════════════════════════ */
function ViewModal({ video, onClose }) {
  if (!video) return null;

  const embedUrl = getEmbedUrl(video.video_url);

  /* Parse show_on_pages — stored as comma-separated string in DB */
  const pages = video.show_on_pages
    ? video.show_on_pages.split(",").map((p) => p.trim()).filter(Boolean)
    : [];

  return (
    <div className="avd-modal-overlay" onClick={onClose}>
      <div className="avd-modal avd-modal--view" onClick={(e) => e.stopPropagation()}>
        <div className="avd-modal__header">
          <div className="avd-modal__title-row">
            <span className="avd-modal__tag">VIEW</span>
            <h2 className="avd-modal__title">{video.title || "Video"}</h2>
          </div>
          <button className="avd-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="avd-modal__body avd-modal__body--view">
          {/* Embedded player or placeholder */}
          {embedUrl ? (
            <div className="avd-view-player">
              <iframe
                src={embedUrl}
                title={video.title || "Video"}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="avd-view-player--placeholder"><span>▶</span></div>
          )}

          {/* Details grid */}
          <div className="avd-view-grid">
            {[
              ["Title",   video.title  || "—"],
              ["Order",   video.order  ?? "—"],
              ["Status",  Number(video.active) === 1 ? "Active" : "Inactive"],
              ["Created", video.created_at ? new Date(video.created_at).toLocaleDateString("en-ZA") : "—"],
              ["Updated", video.updated_at ? new Date(video.updated_at).toLocaleDateString("en-ZA") : "—"],
              ["Video ID", extractYouTubeId(video.video_url) || "—"],
            ].map(([label, val]) => (
              <div key={label} className="avd-view-row">
                <span className="avd-view-row__label">{label}</span>
                <span className="avd-view-row__val">{val}</span>
              </div>
            ))}
            {/* Full-width URL row */}
            <div className="avd-view-row" style={{ gridColumn: "1 / -1" }}>
              <span className="avd-view-row__label">URL</span>
              <span className="avd-view-row__val">
                <a href={video.video_url} target="_blank" rel="noopener noreferrer">
                  {video.video_url}
                </a>
              </span>
            </div>
          </div>

          {/* Pages */}
          <div className="avd-view-section">
            <h4 className="avd-view-section__title">Show On Pages</h4>
            <div className="avd-view-pages">
              <PagesPills pages={pages} />
            </div>
          </div>

          {/* Description */}
          {video.description && (
            <div className="avd-view-section">
              <h4 className="avd-view-section__title">Description</h4>
              <p className="avd-view-section__text">{video.description}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   CREATE / EDIT MODAL
   ═══════════════════════════════════ */
function FormModal({ mode, video, onClose, onSaved }) {
  const [form,     setForm]     = useState(EMPTY_FORM);
  const [preview,  setPreview]  = useState(null);
  const [clearImg, setClearImg] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState("");

  /* Populate when editing */
  useEffect(() => {
    if (mode === "edit" && video) {
      const pages = video.show_on_pages
        ? video.show_on_pages.split(",").map((p) => p.trim()).filter(Boolean)
        : [];
      setForm({
        title:         video.title        || "",
        description:   video.description  || "",
        video_url:     video.video_url    || "",
        show_on_pages: pages,
        order:         video.order        ?? "999",
        active:        Number(video.active) === 1 ? 1 : 0,
        thumbnail:     null,
      });
      setPreview(video.thumbnail_filename ? thumbImg(video.thumbnail_filename) : null);
      setClearImg(false);
    }
  }, [mode, video]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleFile = (file) => {
    if (!file) return;
    set("thumbnail", file);
    setClearImg(false);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleClearThumb = () => {
    set("thumbnail", null);
    setPreview(null);
    setClearImg(true);
  };

  const videoId   = extractYouTubeId(form.video_url);
  const urlValid  = !form.video_url || !!videoId;

  const handleSubmit = async () => {
    setError("");
    if (!form.title.trim())    return setError("Video title is required.");
    if (!form.video_url.trim()) return setError("YouTube URL is required.");
    if (!videoId)              return setError("Could not extract a valid YouTube video ID from the URL.");

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title",         form.title.trim());
      fd.append("description",   form.description || "");
      fd.append("video_url",     form.video_url.trim());
      fd.append("show_on_pages", form.show_on_pages.join(","));
      fd.append("order",         parseInt(form.order) || 999);
      fd.append("active",        form.active);
      if (form.thumbnail) fd.append("thumbnail_filename", form.thumbnail);
      if (clearImg)        fd.append("clear_thumbnail", "1");

      if (mode === "create") {
        await apiFetch("/api/admin/videos", { method: "POST", body: fd });
      } else {
        await apiFetch(`/api/admin/videos/${video.id}`, { method: "PUT", body: fd });
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="avd-modal-overlay" onClick={onClose}>
      <div className="avd-modal avd-modal--form" onClick={(e) => e.stopPropagation()}>
        <div className="avd-modal__header">
          <div className="avd-modal__title-row">
            <span className="avd-modal__tag">{mode === "create" ? "NEW" : "EDIT"}</span>
            <h2 className="avd-modal__title">
              {mode === "create" ? "Add Video" : "Edit Video"}
            </h2>
          </div>
          <button className="avd-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="avd-modal__body avd-modal__body--form">
          {error && <div className="avd-modal__error">⚠ {error}</div>}

          {/* ── Section: Video Details ── */}
          <div className="avd-form-section">
            <h3 className="avd-form-section__title">Video Details</h3>
            <div className="avd-form-grid">
              <div className="avd-form-field avd-form-field--full">
                <label>Title <span className="req">*</span></label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  placeholder="e.g. Workshop Tour 2024"
                />
              </div>

              <div className="avd-form-field avd-form-field--full">
                <label>YouTube URL <span className="req">*</span></label>
                <div className="avd-url-input-wrap">
                  <span className="avd-url-input-wrap__prefix">▶</span>
                  <input
                    type="url"
                    value={form.video_url}
                    onChange={(e) => set("video_url", e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=5MAiZWfgjsM"
                  />
                </div>
                {form.video_url && (
                  <div className={`avd-video-id-preview ${!urlValid ? "avd-video-id-preview--error" : ""}`}>
                    {urlValid
                      ? <>✓ Video ID: <code style={{ fontFamily: "monospace", marginLeft: 4 }}>{videoId}</code></>
                      : "✗ Could not detect a valid YouTube video ID"}
                  </div>
                )}
                <p className="avd-form-hint">
                  Paste the full YouTube URL. Supports <code>watch?v=</code>, <code>youtu.be/</code>, and <code>shorts/</code> formats.
                </p>
              </div>

              <div className="avd-form-field avd-form-field--full">
                <label>Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="Optional description or caption for this video…"
                />
              </div>
            </div>
          </div>

          {/* ── Section: Thumbnail ── */}
          <div className="avd-form-section">
            <h3 className="avd-form-section__title">Thumbnail</h3>
            <label className="avd-image-upload">
              {preview ? (
                <div className="avd-image-upload__preview-wrap">
                  <img src={preview} alt="Thumbnail preview" />
                </div>
              ) : (
                <div className="avd-image-upload__placeholder">
                  <span>🎬</span>
                  <span>Click to upload thumbnail</span>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => handleFile(e.target.files[0])}
              />
              <span className="avd-image-upload__label">
                {preview ? "Click to change thumbnail" : "Upload Thumbnail"}
              </span>
            </label>
            {preview && (
              <button
                type="button"
                className="avd-image-clear"
                style={{ marginTop: 6 }}
                onClick={handleClearThumb}
              >
                Remove thumbnail
              </button>
            )}
            <p className="avd-form-hint">
              Saved to <code>src/assets/uploads/thumbnails/</code>. Leave blank to keep existing.
              Recommended ratio: 16:9.
            </p>
          </div>

          {/* ── Section: Page Assignment ── */}
          <div className="avd-form-section">
            <h3 className="avd-form-section__title">Page Assignment</h3>
            <div className="avd-form-field">
              <label>Show On Pages</label>
              <PagesInput
                value={form.show_on_pages}
                onChange={(val) => set("show_on_pages", val)}
              />
              <p className="avd-form-hint">
                Type a page slug (e.g. <code>home</code>, <code>about-us</code>, <code>services</code>)
                and press Enter to add. Stored as a comma-separated list.
              </p>
            </div>
          </div>

          {/* ── Section: Display Options ── */}
          <div className="avd-form-section">
            <h3 className="avd-form-section__title">Display Options</h3>
            <div className="avd-form-grid">
              <div className="avd-form-field">
                <label>Display Order</label>
                <input
                  type="number"
                  min="0"
                  value={form.order}
                  onChange={(e) => set("order", e.target.value)}
                  placeholder="999"
                />
                <p className="avd-form-hint">Lower numbers appear first.</p>
              </div>

              <div className="avd-form-field avd-form-field--check-col">
                <label className="avd-form-label--block">Visibility</label>
                <label className="avd-check">
                  <input
                    type="checkbox"
                    checked={Number(form.active) === 1}
                    onChange={(e) => set("active", e.target.checked ? 1 : 0)}
                  />
                  <span>Active (visible on site)</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="avd-modal__footer">
          <button className="avd-btn avd-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="avd-btn avd-btn--save" onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving…" : mode === "create" ? "Add Video" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   DELETE MODAL
   ═══════════════════════════════════ */
function DeleteModal({ video, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error,    setError]    = useState("");

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await apiFetch(`/api/admin/videos/${video.id}`, { method: "DELETE" });
      onDeleted();
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  return (
    <div className="avd-modal-overlay" onClick={onClose}>
      <div className="avd-modal avd-modal--delete" onClick={(e) => e.stopPropagation()}>
        <div className="avd-delete__icon">🗑</div>
        <h2 className="avd-delete__title">Delete Video?</h2>
        <p className="avd-delete__body">
          You are about to permanently delete{" "}
          <strong>{video?.title || "this video"}</strong>. The thumbnail file will also
          be removed from disk. This action cannot be undone.
        </p>
        {error && <div className="avd-modal__error">⚠ {error}</div>}
        <div className="avd-delete__actions">
          <button className="avd-btn avd-btn--cancel" onClick={onClose} disabled={deleting}>
            Cancel
          </button>
          <button className="avd-btn avd-btn--delete" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Yes, Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
export default function AdminVideos() {
  const [videos,     setVideos]     = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");

  /* Filters */
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sort,         setSort]         = useState("order_asc");
  const [page,         setPage]         = useState(1);
  const searchTimer = useRef(null);

  /* Modals */
  const [modal,  setModal]  = useState(null);
  const [target, setTarget] = useState(null);

  /* ── Load videos ── */
  const loadVideos = useCallback(() => {
    setLoading(true);
    setError("");
    const q = new URLSearchParams();
    if (search)       q.set("search", search);
    if (statusFilter) q.set("active", statusFilter);
    q.set("sort",  sort);
    q.set("page",  page);
    q.set("limit", "15");

    apiFetch(`/api/admin/videos?${q}`)
      .then((data) => {
        setVideos(data.videos       || []);
        setPagination(data.pagination || { page: 1, total: 0, pages: 1 });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [search, statusFilter, sort, page]);

  useEffect(() => { loadVideos(); }, [loadVideos]);

  /* ── Handlers ── */
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setPage(1), 400);
  };

  const openModal  = (type, vid = null) => { setModal(type); setTarget(vid); };
  const closeModal = () => { setModal(null); setTarget(null); };
  const afterSave  = () => { closeModal(); loadVideos(); };

  return (
    <AdminLayout pageTag="CONTENT" pageTitle="Videos">

      {error && <div className="avd-error-bar">⚠ {error}</div>}

      {/* ── Toolbar ── */}
      <div className="avd-toolbar">
        <div className="avd-toolbar__left">
          <div className="avd-search-wrap">
            <span className="avd-search-wrap__icon">🔍</span>
            <input
              className="avd-search"
              type="text"
              placeholder="Search by title or description…"
              value={search}
              onChange={handleSearchChange}
            />
            {search && (
              <button
                className="avd-search-wrap__clear"
                onClick={() => { setSearch(""); setPage(1); }}
              >✕</button>
            )}
          </div>

          <select
            className="avd-filter-select"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>

          <select
            className="avd-filter-select"
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1); }}
          >
            <option value="order_asc">Order ↑</option>
            <option value="order_desc">Order ↓</option>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="title_asc">Title A–Z</option>
          </select>
        </div>

        <div className="avd-toolbar__right">
          <span className="avd-toolbar__count">
            {loading
              ? "…"
              : `${pagination.total} video${pagination.total !== 1 ? "s" : ""}`}
          </span>
          <button className="avd-btn-primary" onClick={() => openModal("create")}>
            + Add Video
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="avd-table-skeleton" />
      ) : videos.length === 0 ? (
        <div className="avd-empty">
          <span>🎬</span>
          <p>No videos found.</p>
          {(search || statusFilter) && (
            <button
              className="avd-btn avd-btn--cancel"
              onClick={() => { setSearch(""); setStatusFilter(""); setPage(1); }}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="avd-table-wrap">
          <table className="avd-table">
            <thead>
              <tr>
                <th>Thumbnail</th>
                <th>Title</th>
                <th>Description</th>
                <th>Pages</th>
                <th>Order</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {videos.map((vid) => {
                const pages = vid.show_on_pages
                  ? vid.show_on_pages.split(",").map((p) => p.trim()).filter(Boolean)
                  : [];

                return (
                  <tr key={vid.id}>
                    {/* Thumbnail */}
                    <td>
                      {vid.thumbnail_filename ? (
                        <div className="avd-thumb-wrap">
                          <img src={thumbImg(vid.thumbnail_filename)} alt={vid.title || ""} />
                          <div className="avd-thumb-play"><YTPlayIcon /></div>
                        </div>
                      ) : (
                        <div className="avd-thumb-placeholder">▶</div>
                      )}
                    </td>

                    {/* Title + URL pill */}
                    <td>
                      <div className="avd-title-cell">
                        <span className="avd-title-cell__name">{vid.title || "—"}</span>
                        {vid.video_url && (
                          <span className="avd-url-pill">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2 31.6 31.6 0 0 0 0 12a31.6 31.6 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1A31.6 31.6 0 0 0 24 12a31.6 31.6 0 0 0-.5-5.8zM9.5 15.6V8.4l6.3 3.6-6.3 3.6z"/>
                            </svg>
                            YouTube
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Description */}
                    <td>
                      <span className="avd-desc-preview">
                        {vid.description
                          ? vid.description.slice(0, 90) + (vid.description.length > 90 ? "…" : "")
                          : "—"}
                      </span>
                    </td>

                    {/* Pages */}
                    <td>
                      <div className="avd-pages-cell">
                        <PagesPills pages={pages} />
                      </div>
                    </td>

                    {/* Order */}
                    <td>
                      <span className="avd-order-badge">{vid.order ?? 999}</span>
                    </td>

                    {/* Status */}
                    <td><StatusBadge active={vid.active} /></td>

                    {/* Actions */}
                    <td>
                      <DotMenu
                        onView={()   => openModal("view",   vid)}
                        onEdit={()   => openModal("edit",   vid)}
                        onDelete={() => openModal("delete", vid)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ── */}
      {pagination.pages > 1 && (
        <div className="avd-pagination">
          <button
            className="avd-pagination__btn"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >← Prev</button>
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`avd-pagination__btn ${p === page ? "active" : ""}`}
              onClick={() => setPage(p)}
            >{p}</button>
          ))}
          <button
            className="avd-pagination__btn"
            disabled={page >= pagination.pages}
            onClick={() => setPage((p) => p + 1)}
          >Next →</button>
        </div>
      )}

      {/* ── Modals ── */}
      {modal === "view"   && <ViewModal   video={target}  onClose={closeModal} />}
      {modal === "create" && <FormModal   mode="create"   onClose={closeModal} onSaved={afterSave} />}
      {modal === "edit"   && <FormModal   mode="edit"     video={target} onClose={closeModal} onSaved={afterSave} />}
      {modal === "delete" && <DeleteModal video={target}  onClose={closeModal} onDeleted={afterSave} />}

    </AdminLayout>
  );
}